import { atxSdkUrl } from "config/constant";

/**
 * The slice of the ATX (v8) SDK's public API this client calls.
 *
 * Hand-declared on purpose: the SDK ships as a bundle loaded at runtime, not
 * as a dependency, so there is nothing to import types from. Keep this in step
 * with `embedded/atx/src/api/types.ts` — a mismatch surfaces as a rejected
 * `session.init()` rather than a compile error.
 */
export interface AtxAttendee {
  identifier?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  [extra: string]: unknown;
}

export interface AtxWorkflowStep {
  identifier?: string;
  /** ATX's own field for the flow uuid, alongside `identifier`. */
  flow_uuid?: string;
  name?: string;
  duration?: number;
  [extra: string]: unknown;
}

export interface AtxSessionInitConfig {
  dsn: string;
  identifier: string;
  attendee: AtxAttendee;
  workflow_step: AtxWorkflowStep;
  language?: string;
}

export interface AtxSessionOutput {
  uuid?: string;
  state?: string;
  identifier?: string;
}

export type AtxNetworkStatus = { status: "online" | "offline" };

export interface AtxSessionApi {
  init: (config: AtxSessionInitConfig) => Promise<boolean>;
  start: () => Promise<AtxSessionOutput>;
  stop: () => Promise<AtxSessionOutput>;
  complete: () => Promise<AtxSessionOutput>;
  pause: (reason: string) => Promise<void>;
  resume: (reason: string) => Promise<void>;
  getState: () => AtxSessionOutput | null;
  /** Returns its own unsubscribe function. */
  on: <T = unknown>(eventName: string, callback: (payload: T) => void) => () => void;
}

export interface AtxSdk {
  session?: AtxSessionApi;
  onError?: (callback: (error: unknown) => void) => Promise<void>;
  getVersion?: () => string;
  destroy?: () => void;
}

declare global {
  interface Window {
    Proview?: AtxSdk;
  }
}

const SCRIPT_ID = "proview-atx-sdk";

/**
 * `onerror` never fires for a request that hangs (a CDN black-holed by a
 * corporate proxy), so the load needs its own deadline.
 */
const LOAD_TIMEOUT_MS = 15000;

const isUsable = () => typeof window.Proview?.session?.init === "function";

const injectAtxBundle = (url: string): Promise<void> =>
  new Promise((resolve, reject) => {
    // A tag without a usable `window.Proview` behind it never finished loading;
    // drop it rather than waiting on a script that may already have failed.
    document.getElementById(SCRIPT_ID)?.remove();

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = url;

    const timeout = setTimeout(() => {
      script.onload = null;
      script.onerror = null;
      // Drop the tag too: a late success would otherwise populate
      // `window.Proview` after the caller has treated the load as failed.
      script.remove();
      reject(new Error(`ATX bundle timed out after ${LOAD_TIMEOUT_MS}ms: ${url}`));
    }, LOAD_TIMEOUT_MS);

    script.onload = () => {
      clearTimeout(timeout);
      if (!isUsable()) {
        reject(new Error(`ATX bundle loaded without window.Proview.session.init: ${url}`));
        return;
      }
      resolve();
    };

    script.onerror = () => {
      clearTimeout(timeout);
      reject(new Error(`ATX bundle failed to load: ${url}`));
    };

    document.head.appendChild(script);
  });

let inFlight: Promise<void> | null = null;

/**
 * Resolves only for a bundle that is actually usable — one that loaded without
 * exposing `session.init` is a failed load, not a loaded one. That keeps the
 * failure ahead of any teardown, so this client stays on screen.
 *
 * Concurrent calls share one load: injecting twice would have the second call
 * remove the first call's `<script>` mid-flight, leaving it to time out on a
 * tag that is no longer in the document.
 */
export const loadAtxBundle = (url: string = atxSdkUrl): Promise<void> => {
  if (isUsable()) return Promise.resolve();

  if (inFlight) return inFlight;

  const load = injectAtxBundle(url);
  inFlight = load;
  const clear = () => {
    if (inFlight === load) inFlight = null;
  };
  load.then(clear, clear);

  return load;
};
